import { envelopeDetails, envelopeEntry, PbApiError, readFetchFailure } from 'pb-api-errors';
import { secondFactorReasonOf, type SecondFactorReason } from 'pb-auth-contract';

export { envelopeReason, memberFacingMessage } from 'pb-api-errors';

export type ValidationErrors = Record<string, string[]>;

/** What a failed call carries besides its sentence and status; every field is optional. */
export interface ApiErrorDetails {
    errors?: ValidationErrors;
    reason?: string | null;
    secondFactor?: SecondFactorReason | null;
    envelopeDetails?: Readonly<Record<string, unknown>>;
    retryAfter?: number | null;
}

/**
 * A failed call to app-api. `reason` (`PbApiError`) is the envelope's machine-readable half, and its
 * presence is what proves the API wrote `message` for a person (`memberFacingMessage`,
 * `pb-api-errors`). `retryAfter` (`PbApiError`) is the wait, in whole seconds, a refusal named in
 * its `Retry-After` (a throttled route's 429); null when it named none.
 */
export class ApiError extends PbApiError {
    /**
     * Field errors from a 422, when the response carried them.
     *
     * Kept because the message alone cannot tell one rejection from another: a
     * caller that has to react differently to a particular field — offering to
     * add a phone number unverified when its destination is one we refuse to
     * message, say — would otherwise be reduced to matching on prose.
     */
    public errors: ValidationErrors;

    /**
     * Why a route that checks the member's authenticator code refused it, when it did — the
     * failure body's `data.reason` (`pb-auth-contract`).
     *
     * Kept apart from `reason`: that one proves the API wrote `message` for a person, and a
     * second-factor refusal carries no sentence at all, so the caller words it.
     */
    readonly secondFactor: SecondFactorReason | null;

    /**
     * What the first entry of the v3 error body carries besides its status, code, sentence and
     * reason (`envelopeDetails`, `pb-api-errors`): the fields a refusal hands its caller to act
     * on, such as the invoice pay route's `invoice_status`. Empty when the answer was not that
     * body or the entry carries none.
     */
    readonly envelopeDetails: Readonly<Record<string, unknown>>;

    constructor(
        message: string,
        code: number = 500,
        {
            errors = {},
            reason = null,
            secondFactor = null,
            envelopeDetails: details = {},
            retryAfter = null,
        }: ApiErrorDetails = {},
    ) {
        super(message, code, reason, retryAfter);
        this.errors = errors;
        this.secondFactor = secondFactor;
        this.envelopeDetails = details;
    }

    /** Whether the response rejected the given field. */
    public rejected(field: string): boolean {
        return (this.errors[field]?.length ?? 0) > 0;
    }

    /**
     * A failure a service read off a `usePbApi` answer that came back with the wrong status.
     *
     * `usePbApi` resolves those rather than throwing, so the service holds the status, the
     * flattened message, the raw body and any wait the API named, and this is where they become
     * one error. A v3 envelope keeps its own sentence, empty when it has none, so
     * `memberFacingMessage` gives the caller's sentence rather than this fallback with the
     * envelope's reason vouching for it. The envelope's own fields ride along as
     * `envelopeDetails`.
     */
    static fromEnvelope(
        status: number,
        message: string | null | undefined,
        payload: unknown,
        fallback: string,
        retryAfter: number | null = null,
    ): ApiError {
        const errors = (payload as { errors?: unknown } | null | undefined)?.errors;
        const entry = envelopeEntry(payload);

        return new ApiError(entry ? (entry.message ?? '') : (message || fallback), status, {
            errors: isFieldErrors(errors) ? errors : {},
            reason: entry?.reason ?? null,
            secondFactor: secondFactorReasonOf(payload),
            envelopeDetails: envelopeDetails(payload),
            retryAfter,
        });
    }

    /**
     * The error a failed `$fetch` stands for, read through `readFetchFailure`: its status, its
     * field map, any wait it named, and, when the server answered in the v3 envelope, that
     * envelope's sentence (empty when it has none), reason and own fields.
     */
    static fromFetchError(error: unknown): ApiError {
        // An ApiError already carries a deliberate status on `code`, which none of the
        // fetch-shaped lookups below can see — re-normalizing it would collapse it to 500.
        if (error instanceof ApiError) {
            return error;
        }

        const failure = readFetchFailure(error);
        const errors = (failure.body as { errors?: unknown } | null | undefined)?.errors;

        return new ApiError(failure.message, failure.status ?? 500, {
            errors: isFieldErrors(errors) ? errors : {},
            reason: failure.entry?.reason ?? null,
            envelopeDetails: envelopeDetails(failure.body),
            retryAfter: failure.retryAfter,
        });
    }
}

/**
 * The API uses `errors` for two shapes: Laravel's field map, and an array of
 * `{message}` objects that usePbApi already flattens into the message. Only the
 * former is a field map.
 */
function isFieldErrors(errors: unknown): errors is ValidationErrors {
    return typeof errors === 'object' && errors !== null && !Array.isArray(errors);
}
